import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import AdminShell from "./AdminShell";
import FormattedNumber from "../Common/FormattedNumber";
// No external chart library — using inline SVG

const API = process.env.REACT_APP_API_URL || "";
const card = { background: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" };
const formatSAR = (val) => `﷼ ${(val || 0).toLocaleString("en-SA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// FormattedNumber is used directly in JSX for large display values

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCounts, setPendingCounts] = useState({ pendingFunds: 0, pendingWithdrawals: 0, pendingProperties: 0, total: 0 });
  const [chartTab, setChartTab] = useState('transactions'); // 'transactions' | 'revenue'
  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;

  const fetchPendingCounts = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/api/getAdminPendingCounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data.status === 1) setPendingCounts(res.data); })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/api/getAdminAnalytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data.status === 1) setAnalytics(res.data); setLoading(false); })
      .catch(() => setLoading(false));
    fetchPendingCounts();
  }, [token, fetchPendingCounts]);

  const stats = analytics ? [
    { label: "Total Funds in Wallets", value: formatSAR(analytics.totalFunds), rawValue: analytics.totalFunds, color: "#c9a84c", icon: "💰" },
    { label: "Total Users", value: analytics.totalUsers, color: "#3b82f6", icon: "👥" },
    { label: "Total Properties", value: analytics.totalProperties, color: "#0e3725", icon: "🏢" },
    { label: "Transaction Volume", value: formatSAR(analytics.totalTransactionValue), rawValue: analytics.totalTransactionValue, color: "#8b5cf6", icon: "📊" },
  ] : [];

  const feeCards = analytics ? [
    { label: "Listing Fees", rate: "2%", rawValue: analytics.listingFees, desc: "2% of listed property value", color: "#c9a84c" },
    { label: "Subscription Fees", rate: "1%", rawValue: analytics.subscriptionFees, desc: "1% on each subscription buy", color: "#10b981" },
    { label: "Exchange Fees", rate: "0.5%", rawValue: analytics.exchangeFees, desc: "0.25% from each party per trade", color: "#3b82f6" },
    { label: "Total Revenue", rate: "", rawValue: (analytics.listingFees || 0) + (analytics.subscriptionFees || 0) + (analytics.exchangeFees || 0), desc: "Combined platform fees", color: "#0e3725" },
  ] : [];

  const quickActions = [
    { label: "List New Property", to: "/admin/createproperty", color: "#1a2035", icon: "🏗️" },
    { label: "Manage Users", to: "/admin/users", color: "#c9a84c", icon: "👥" },
    { label: "Fund Requests", to: "/admin/requestFund", color: "#16a34a", icon: "💰" },
    { label: "Blockchain Data", to: "/admin/blockchaindata", color: "#6366f1", icon: "⛓️" },
  ];

  return (
    <AdminShell pageTitle="Dashboard">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .fee-card { background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.07); border-left: 4px solid; }
        .tx-table { width: 100%; border-collapse: collapse; }
        .tx-table th { background: #f9fafb; padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb; }
        .tx-table td { padding: 12px 14px; font-size: 13px; color: #374151; border-bottom: 1px solid #f3f4f6; }
        .badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; }
        .badge-buy { background: #d1fae5; color: #065f46; }
        .badge-sell { background: #fee2e2; color: #991b1b; }
      `}</style>

      {/* Welcome Banner */}
      <div style={{ background: "linear-gradient(135deg,#1a2035 0%,#0e3725 100%)", borderRadius: 14, padding: "28px 32px", marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#fff" }}>Welcome back, Admin</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "rgba(255,255,255,0.6)" }}>Treasury Real Estate Tokenization Platform</p>
        </div>
        <Link to="/admin/createproperty" style={{ background: "#c9a84c", color: "#fff", borderRadius: 9, padding: "10px 22px", fontSize: 13.5, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
          + List Property
        </Link>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
          <div style={{ width: 40, height: 40, border: "4px solid #e5e7eb", borderTopColor: "#c9a84c", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
          Loading analytics...
        </div>
      ) : (
        <>
          {/* Stats Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
            {stats.map((s) => (
              <div key={s.label} style={{ ...card, padding: "20px 22px", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: `${s.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                  {s.icon}
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</p>
                  <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 800, color: s.color }}>
                    {typeof s.rawValue === 'number'
                      ? <FormattedNumber value={s.rawValue} prefix="﷼ " decimals={0} decimalSize="0.7em" />
                      : s.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pending Actions Widget */}
          {pendingCounts.total > 0 && (
            <div style={{ ...card, padding: "22px 24px", marginBottom: 24, border: "1.5px solid #fde68a" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚠️</div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Pending Actions</h3>
                    <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{pendingCounts.total} item{pendingCounts.total > 1 ? "s" : ""} require your attention</p>
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12 }}>
                {pendingCounts.pendingFunds > 0 && (
                  <Link to="/admin/requestFund" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 10, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.2)" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(217,119,6,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>💰</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#92400e" }}>{pendingCounts.pendingFunds} Fund Request{pendingCounts.pendingFunds > 1 ? "s" : ""}</p>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: "#b45309" }}>Pending approval</p>
                    </div>
                    <svg width="14" height="14" fill="none" stroke="#d97706" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
                  </Link>
                )}
                {pendingCounts.pendingWithdrawals > 0 && (
                  <Link to="/admin/withdrawals" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 10, background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🏦</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#4338ca" }}>{pendingCounts.pendingWithdrawals} Withdrawal{pendingCounts.pendingWithdrawals > 1 ? "s" : ""}</p>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: "#6366f1" }}>Pending processing</p>
                    </div>
                    <svg width="14" height="14" fill="none" stroke="#6366f1" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
                  </Link>
                )}
                {pendingCounts.pendingProperties > 0 && (
                  <Link to="/admin/properties" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 10, background: "rgba(14,55,37,0.06)", border: "1px solid rgba(14,55,37,0.2)" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(14,55,37,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🏢</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0e3725" }}>{pendingCounts.pendingProperties} Property Listing{pendingCounts.pendingProperties > 1 ? "s" : ""}</p>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: "#166534" }}>Under review</p>
                    </div>
                    <svg width="14" height="14" fill="none" stroke="#0e3725" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Transaction Volume / Revenue Chart */}
          <div style={{ ...card, padding: "24px", marginBottom: 24 }}>
            {/* Tab header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
                {chartTab === 'transactions' ? 'Transaction Activity (Last 30 Days)' : 'Daily Revenue (Last 30 Days)'}
              </h3>
              <div style={{ display: 'flex', gap: 6, background: '#f3f4f6', borderRadius: 8, padding: 4 }}>
                <button onClick={() => setChartTab('transactions')} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: chartTab === 'transactions' ? '#0e3725' : 'transparent', color: chartTab === 'transactions' ? '#fff' : '#6b7280', transition: 'all 0.15s' }}>Transactions</button>
                <button onClick={() => setChartTab('revenue')} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: chartTab === 'revenue' ? '#0e3725' : 'transparent', color: chartTab === 'revenue' ? '#fff' : '#6b7280', transition: 'all 0.15s' }}>Revenue</button>
              </div>
            </div>
            {(() => {
              const isRevenue = chartTab === 'revenue';
              const rawPts = isRevenue ? (analytics?.revenueByDate || []) : (analytics?.txByDate || []);
              const pts = rawPts;
              if (!analytics || pts.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>📈</div>
                    <p style={{ margin: 0 }}>No data yet. Activity will appear here once users start trading.</p>
                  </div>
                );
              }
              const accent = isRevenue ? '#10b981' : '#c9a84c';
              const gradId = isRevenue ? 'chartGradRev' : 'chartGradTx';
              const getVal = (p) => isRevenue ? (p.revenue || 0) : (p.value || 0);
              const maxVal = Math.max(...pts.map(getVal), 1);
              // Y-axis: 5 reference lines with nice rounded labels
              const yTicks = [0, 0.25, 0.5, 0.75, 1];
              const YPAD_LEFT = 64; // space for Y-axis labels
              const W = 800, H = 200, XPAD = 10, YPAD_TOP = 12, YPAD_BOT = 24;
              const chartW = W - YPAD_LEFT - XPAD;
              const chartH = H - YPAD_TOP - YPAD_BOT;
              const xs = pts.map((_, i) => YPAD_LEFT + (i / Math.max(pts.length - 1, 1)) * chartW);
              const ys = pts.map(p => YPAD_TOP + (1 - getVal(p) / maxVal) * chartH);
              const lineD = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
              const areaD = lineD + ` L${xs[xs.length-1]},${YPAD_TOP + chartH} L${YPAD_LEFT},${YPAD_TOP + chartH} Z`;
              const fmtYLabel = (v) => isRevenue
                ? (v >= 1000 ? `﷼${(v/1000).toFixed(1)}k` : `﷼${v.toFixed(0)}`)
                : (v >= 1000 ? `${(v/1000).toFixed(1)}k` : `${v.toFixed(0)}`);
              return (
                <div style={{ overflowX: 'auto' }}>
                  <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 220 }}>
                    <defs>
                      <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
                        <stop offset="100%" stopColor={accent} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {/* Y-axis grid lines + labels */}
                    {yTicks.map((t, i) => {
                      const yPos = YPAD_TOP + t * chartH;
                      const labelVal = maxVal * (1 - t);
                      return (
                        <g key={i}>
                          <line x1={YPAD_LEFT} y1={yPos} x2={W - XPAD} y2={yPos} stroke="#f3f4f6" strokeWidth="1" />
                          <text x={YPAD_LEFT - 6} y={yPos + 4} textAnchor="end" fontSize="9" fill="#9ca3af">{fmtYLabel(labelVal)}</text>
                        </g>
                      );
                    })}
                    <path d={areaD} fill={`url(#${gradId})`} />
                    <path d={lineD} fill="none" stroke={accent} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    {xs.map((x, i) => (
                      <circle key={i} cx={x} cy={ys[i]} r="3" fill={accent} />
                    ))}
                    {/* X-axis date labels */}
                    {pts.filter((_, i) => i % Math.ceil(pts.length / 6) === 0).map((p, i) => {
                      const origIdx = pts.indexOf(p);
                      return <text key={i} x={xs[origIdx]} y={H - 4} textAnchor="middle" fontSize="9" fill="#9ca3af">{new Date(p.date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</text>;
                    })}
                  </svg>
                </div>
              );
            })()
            }

          </div>

          {/* Fee Breakdown */}
          <div style={{ ...card, padding: "24px", marginBottom: 24 }}>
            <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: "#111827" }}>Platform Revenue & Fees</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 20 }}>
              {feeCards.map((f, i) => (
                <div key={i} className="fee-card" style={{ borderLeftColor: f.color }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{f.label}</span>
                    {f.rate && <span style={{ fontSize: 13, fontWeight: 700, color: f.color }}>{f.rate}</span>}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: f.color, marginBottom: 4 }}>
                    <FormattedNumber value={f.rawValue} prefix="﷼ " decimals={2} decimalSize="0.7em" />
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{f.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ ...card, padding: "22px 24px", marginBottom: 24 }}>
            <h3 style={{ margin: "0 0 18px", fontSize: 14, fontWeight: 700, color: "#111827" }}>Quick Actions</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12 }}>
              {quickActions.map((a) => (
                <Link key={a.to} to={a.to} style={{ background: `${a.color}0f`, border: `1px solid ${a.color}22`, borderRadius: 10, padding: "16px 18px", textDecoration: "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 22 }}>{a.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: a.color }}>{a.label}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Transactions */}
          <div style={{ ...card, padding: "24px" }}>
            <h3 style={{ margin: "0 0 18px", fontSize: 15, fontWeight: 700, color: "#111827" }}>Recent Transactions</h3>
            {analytics?.recentTransactions?.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table className="tx-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Property</th>
                      <th>Action</th>
                      <th>Units</th>
                      <th>Amount</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.recentTransactions.map((t, i) => (
                      <tr key={i}>
                        <td style={{ color: "#9ca3af", fontSize: 12 }}>{new Date(t.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
                        <td style={{ fontWeight: 500 }}>{t.propertyId?.title || "Unknown"}</td>
                        <td><span className={`badge badge-${t.action}`}>{t.action?.toUpperCase()}</span></td>
                        <td style={{ fontWeight: 600 }}>{(t.units || 0).toLocaleString()}</td>
                        <td style={{ fontWeight: 600, color: t.action === "buy" ? "#ef4444" : "#10b981" }}>
                          {t.action === "buy" ? "-" : "+"}{formatSAR((t.units || 1) * (t.price || 0))}
                        </td>
                        <td><span style={{ fontSize: 12, color: "#6b7280" }}>{t.isSubscription ? "Subscription" : "Exchange"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                <p style={{ margin: 0 }}>No transactions yet.</p>
              </div>
            )}
          </div>
        </>
      )}
    </AdminShell>
  );
};

export default AdminDashboard;
